import { apiBase } from './apiBase';
import { apiFetch, ApiError } from './api';
import { useAuthStore } from '../store/useAuthStore';
import type { DailyTask } from './dailyApi';
import type { CommentAttachment } from './tasksApi';

// ---------------------------------------------------------------------------
// Daily status check-ins — API client.
//
// Mirrors backend/src/modules/daily/status/*:
//   GET    /api/daily-status/me?date=                — my board (+ reminder settings)
//   GET    /api/daily-status/users/:id?date=         — someone's board (scope 'all')
//   PUT    /api/daily-status/me/tasks/:taskId?date=  — set my quick status + comment
//   DELETE /api/daily-status/me/tasks/:taskId?date=  — clear it
//   GET    /api/daily-status/report?userId=&date=    — one person's report (+ markdown)
//   GET    /api/daily-status/report/team?date=       — the team roll-up (scope 'all')
//   GET    /api/daily-status/report(/team)/export    — PDF / markdown download
//   POST   /api/daily-status/report(/team)/send      — email now
//   GET    /api/daily-status/reports?userId=&from=&to= — snapshot history
//   GET/PUT /api/settings/daily-status               — times, on/off, chat sender (ADMIN)
// ---------------------------------------------------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type CheckInStatus = 'DONE' | 'IN_PROGRESS' | 'BLOCKED' | 'NOT_STARTED';

export type TodayReason =
  | 'due_today'
  | 'overdue'
  | 'started'
  | 'worked_on'
  | 'completed_today'
  | 'checked_in';

export interface CheckInView {
  status: CheckInStatus;
  /** The exact configured status confirmed (null for BLOCKED / older rows). */
  taskStatusId: string | null;
  /** Plain-text note (the reason, for BLOCKED). */
  comment: string | null;
  /** The TaskComment holding the note + attachments, when one was written. */
  commentId: string | null;
  attachments: CommentAttachment[];
  updatedAt: string;
}

export interface TodayTask extends DailyTask {
  reasons: TodayReason[];
  /** Counts toward "fără status": no check-in, open, not in review, not worked on that day. */
  needsStatus: boolean;
  hoursToday: number;
  timerRunning: boolean;
  checkIn: CheckInView | null;
}

export interface DailyStatusUser {
  id: string;
  name: string;
  email: string;
}

export interface DailyStatusScheduleView {
  enabled: boolean;
  reminderTime: string;
  reportTime: string;
  timeZone: string;
  /** When the reminder went out for this day (scheduler or Settings push). */
  reminderFiredAt: string | null;
}

export interface DailyStatusBoard {
  user: DailyStatusUser;
  day: string;
  editable: boolean;
  tasks: TodayTask[];
  pending: number;
  checkedIn: number;
  schedule: DailyStatusScheduleView;
}

export type ReportSection = 'done' | 'inProgress' | 'blocked' | 'notStarted';

export interface ReportTask extends TodayTask {
  section: ReportSection;
  unreported: boolean;
}

export interface ReportSummary {
  total: number;
  checkedIn: number;
  unreported: number;
  done: number;
  inProgress: number;
  blocked: number;
  notStarted: number;
  hoursToday: number;
  personal: number;
}

export interface DailyStatusReport {
  user: DailyStatusUser;
  day: string;
  generatedAt: string;
  sections: Record<ReportSection, ReportTask[]>;
  summary: ReportSummary;
  /** True when the report is a frozen snapshot from the 18:00 run. */
  persisted: boolean;
  markdown: string;
}

export interface TeamMemberReport {
  user: DailyStatusUser;
  summary: ReportSummary;
  sections: Record<ReportSection, ReportTask[]>;
  notReported: boolean;
  persisted: boolean;
}

export interface TeamDailyStatusReport {
  day: string;
  generatedAt: string;
  members: TeamMemberReport[];
  idle: DailyStatusUser[];
  totals: {
    members: number;
    reported: number;
    notReported: number;
    done: number;
    inProgress: number;
    blocked: number;
    notStarted: number;
    hoursToday: number;
  };
  markdown: string;
}

export interface DailyReportHistoryItem {
  day: string;
  totalTasks: number;
  checkedIn: number;
  doneCount: number;
  blockedCount: number;
  generatedBy: string;
  emailSentAt: string | null;
  createdAt: string;
}

function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) sp.set(k, v);
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export async function getMyBoard(date?: string): Promise<DailyStatusBoard> {
  const res = await apiFetch<Envelope<DailyStatusBoard>>(`/api/daily-status/me${qs({ date })}`, {
    headers: authHeaders(),
  });
  return res.data;
}

export async function getUserBoard(userId: string, date?: string): Promise<DailyStatusBoard> {
  const res = await apiFetch<Envelope<DailyStatusBoard>>(
    `/api/daily-status/users/${encodeURIComponent(userId)}${qs({ date })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export interface CheckInInput {
  status: CheckInStatus;
  /** The configured status picked; the task moves to exactly this one. */
  taskStatusId?: string | null;
  comment?: string | null;
  /** Files already uploaded through the task upload-url endpoint. */
  attachments?: CommentAttachment[] | null;
}

export async function setCheckIn(
  taskId: string,
  date: string,
  input: CheckInInput,
): Promise<TodayTask> {
  const res = await apiFetch<Envelope<TodayTask>>(
    `/api/daily-status/me/tasks/${encodeURIComponent(taskId)}${qs({ date })}`,
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

export async function clearCheckIn(taskId: string, date: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: boolean }>>(
    `/api/daily-status/me/tasks/${encodeURIComponent(taskId)}${qs({ date })}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

export async function getReport(userId?: string, date?: string): Promise<DailyStatusReport> {
  const res = await apiFetch<Envelope<DailyStatusReport>>(
    `/api/daily-status/report${qs({ userId, date })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function getTeamReport(date?: string): Promise<TeamDailyStatusReport> {
  const res = await apiFetch<Envelope<TeamDailyStatusReport>>(
    `/api/daily-status/report/team${qs({ date })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function sendReport(userId?: string, date?: string): Promise<{ to: string }> {
  const res = await apiFetch<Envelope<{ to: string }>>(
    `/api/daily-status/report/send${qs({ userId, date })}`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

export interface TeamSendResult {
  reports: number;
  individualEmails: number;
  teamEmails: number;
  errors: string[];
}

export async function sendTeamReport(date?: string): Promise<TeamSendResult> {
  const res = await apiFetch<Envelope<TeamSendResult>>(
    `/api/daily-status/report/team/send${qs({ date })}`,
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}

export async function listReportHistory(
  from: string,
  to: string,
  userId?: string,
): Promise<DailyReportHistoryItem[]> {
  const res = await apiFetch<Envelope<DailyReportHistoryItem[]>>(
    `/api/daily-status/reports${qs({ userId, from, to })}`,
    { headers: authHeaders() },
  );
  return res.data;
}

/**
 * Download a report as PDF or markdown. Streams a binary attachment, so this
 * bypasses apiFetch's JSON handling (same pattern as the hours export).
 */
export async function downloadReport(
  target: 'me' | 'team',
  format: 'pdf' | 'md',
  date?: string,
  userId?: string,
): Promise<void> {
  const apiUrl = apiBase();
  const path =
    target === 'team' ? '/api/daily-status/report/team/export' : '/api/daily-status/report/export';
  const res = await fetch(`${apiUrl}${path}${qs({ date, format, userId })}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'Could not export the report.');
  }
  const disposition = res.headers.get('Content-Disposition');
  const match = disposition?.match(/filename="([^"]+)"/);
  const filename = match?.[1] ?? `raport-zilnic.${format}`;
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// --- Settings (ADMIN) --------------------------------------------------------

export interface DailyStatusConfig {
  enabled: boolean;
  reminderTime: string;
  reportTime: string;
  reminderSenderId: string | null;
  timeZone: string;
}

export interface DailyStatusLastRun {
  at: string;
  day: string;
  ok: boolean;
  outcome: string;
  summary: string | null;
  errors: string[];
}

export interface DailyStatusScheduleInfo {
  armed: boolean;
  enabled: boolean;
  reminderTime: string;
  reportTime: string;
  timeZone: string;
  nextReminderAt: string | null;
  nextReportAt: string | null;
  lastReminder: DailyStatusLastRun | null;
  lastReport: DailyStatusLastRun | null;
}

export interface DailyStatusSettings {
  config: DailyStatusConfig;
  schedule: DailyStatusScheduleInfo;
  senderCandidates: Array<{ id: string; name: string; role: string }>;
}

export async function getDailyStatusSettings(): Promise<DailyStatusSettings> {
  const res = await apiFetch<Envelope<DailyStatusSettings>>('/api/settings/daily-status', {
    headers: authHeaders(),
  });
  return res.data;
}

export async function saveDailyStatusSettings(
  input: Partial<Omit<DailyStatusConfig, 'timeZone'>>,
): Promise<Pick<DailyStatusSettings, 'config' | 'schedule'>> {
  const res = await apiFetch<Envelope<Pick<DailyStatusSettings, 'config' | 'schedule'>>>(
    '/api/settings/daily-status',
    { method: 'PUT', headers: authHeaders(), body: JSON.stringify(input) },
  );
  return res.data;
}

/** Fire a run now; with `userId` only that person gets it, marked as a test. */
export async function runDailyStatusNow(
  kind: 'reminder' | 'report',
  userId?: string,
): Promise<DailyStatusLastRun> {
  const res = await apiFetch<Envelope<DailyStatusLastRun>>('/api/settings/daily-status/run', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(userId ? { kind, userId } : { kind }),
  });
  return res.data;
}

export interface PushReminderResult {
  day: string;
  firedAt: string;
  usersWithPending: number;
  names: string[];
}

/** Show the in-app reminder strip to everyone now (no email, no chat). */
export async function pushReminderToApp(): Promise<PushReminderResult> {
  const res = await apiFetch<Envelope<PushReminderResult>>(
    '/api/settings/daily-status/push-reminder',
    { method: 'POST', headers: authHeaders() },
  );
  return res.data;
}
